import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Layout, Typography, Button, Table, Card, Row, Col, Input, Select, 
  Space, Tag, Tooltip, Empty, Spin, Modal as AntModal 
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined,
  ReloadOutlined, FileTextOutlined, CheckCircleOutlined,
  GlobalOutlined, HomeOutlined, SearchOutlined, FilterOutlined
} from '@ant-design/icons';
import { getPolicies, deletePolicy } from '../../api/policyApi';
import { toast } from 'react-toastify';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const PolicyManagement = () => {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: undefined,
    sector: undefined,
    isInternational: undefined,
  });
  const [sectorOptions, setSectorOptions] = useState([]);

  useEffect(() => {
    fetchPolicies();
  }, [filters, searchTerm]);

  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const response = await getPolicies({});
        const allPolicies = Array.isArray(response.data) ? response.data : [];
        const uniqueSectors = Array.from(
          new Set(allPolicies.map(p => p.sector).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
        setSectorOptions(uniqueSectors);
      } catch (error) {
        console.error('Error fetching sectors:', error);
      }
    };
    fetchSectors();
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        search: searchTerm
      };
      
      const response = await getPolicies(params);
      setPolicies(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching policies:', error);
      setPolicies([]);
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    AntModal.confirm({
      title: 'Delete Policy/Scheme',
      content: 'Are you sure you want to delete this policy/scheme? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deletePolicy(id);
          toast.success('Policy deleted successfully');
          fetchPolicies();
        } catch (error) {
          console.error('Error deleting policy:', error);
          toast.error('Failed to delete policy');
        }
      }
    });
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      type: undefined,
      sector: undefined,
      isInternational: undefined,
    });
    setSearchTerm('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const totalPolicies = policies.length;
  const totalSchemes = policies.filter(p => p.type === 'scheme').length;
  const totalRegularPolicies = policies.filter(p => p.type === 'policy').length;
  const internationalCount = policies.filter(p => p.isInternational).length;

  const columns = [
    {
      title: <span style={{ fontFamily: "'Rubik', sans-serif", fontWeight: 600, fontSize: 14 }}>Title</span>,
      dataIndex: 'title',
      key: 'title',
      ellipsis: false,
      sorter: (a, b) => a.title.localeCompare(b.title),
      render: (text, record) => (
        <div style={{ minWidth: 200, maxWidth: 400 }}>
          <div style={{ 
            fontFamily: "'Rubik', sans-serif", 
            fontWeight: 600, 
            fontSize: 14, 
            color: '#141414', 
            marginBottom: 4,
            wordWrap: 'break-word',
            whiteSpace: 'normal'
          }}>
            {text}
          </div>
          <div style={{ 
            fontFamily: "'Rubik', sans-serif", 
            fontSize: 12, 
            color: '#8c8c8c', 
            lineHeight: 1.4,
            wordWrap: 'break-word',
            whiteSpace: 'normal'
          }}>
            {record.description?.substring(0, 100)}{record.description?.length > 100 ? '...' : ''}
          </div>
        </div>
      )
    },
    {
      title: <span style={{ fontFamily: "'Rubik', sans-serif", fontWeight: 600, fontSize: 14 }}>Type</span>,
      dataIndex: 'type',
      key: 'type',
      width: 110,
      responsive: ['md'],
      filters: [
        { text: 'Policy', value: 'policy' },
        { text: 'Scheme', value: 'scheme' }
      ],
      onFilter: (value, record) => record.type === value,
      render: (type) => (
        <Tag 
          color={type === 'policy' ? '#470172' : '#52c41a'} 
          style={{ 
            fontFamily: "'Rubik', sans-serif",
            fontSize: 12,
            fontWeight: 500,
            padding: '4px 12px',
            borderRadius: 6,
            border: 'none'
          }}
        >
          {type?.charAt(0).toUpperCase() + type?.slice(1)}
        </Tag>
      )
    },
    {
      title: <span style={{ fontFamily: "'Rubik', sans-serif", fontWeight: 600, fontSize: 14 }}>Sector</span>,
      dataIndex: 'sector',
      key: 'sector',
      width: 130,
      responsive: ['lg'],
      filters: [
        { text: 'Agriculture', value: 'agriculture' },
        { text: 'Education', value: 'education' },
        { text: 'Healthcare', value: 'healthcare' },
        { text: 'Technology', value: 'technology' },
        { text: 'Infrastructure', value: 'infrastructure' }
      ],
      onFilter: (value, record) => record.sector === value,
      render: (sector) => (
        <span style={{ fontFamily: "'Rubik', sans-serif", fontSize: 13, color: '#595959', textTransform: 'capitalize' }}>
          {sector || 'N/A'}
        </span>
      )
    },
    {
      title: <span style={{ fontFamily: "'Rubik', sans-serif", fontWeight: 600, fontSize: 14 }}>Scope</span>,
      dataIndex: 'isInternational',
      key: 'isInternational',
      width: 130,
      responsive: ['lg'],
      filters: [
        { text: 'International', value: true },
        { text: 'National', value: false }
      ],
      onFilter: (value, record) => record.isInternational === value,
      render: (isInternational) => (
        <Tag
          icon={isInternational ? <GlobalOutlined /> : <HomeOutlined />}
          color={isInternational ? '#1890ff' : '#fa8c16'}
          style={{ 
            fontFamily: "'Rubik', sans-serif",
            fontSize: 12,
            fontWeight: 500,
            padding: '4px 12px',
            borderRadius: 6,
            border: 'none'
          }}
        >
          {isInternational ? 'International' : 'National'}
        </Tag>
      )
    },
    {
      title: <span style={{ fontFamily: "'Rubik', sans-serif", fontWeight: 600, fontSize: 14 }}>Release Date</span>,
      dataIndex: 'releaseDate',
      key: 'releaseDate',
      width: 130,
      responsive: ['md'],
      render: (date, record) => (
        <span style={{ fontFamily: "'Rubik', sans-serif", fontSize: 13, color: '#595959' }}>
          {formatDate(date || record.createdAt)}
        </span>
      ),
      sorter: (a, b) => new Date(a.releaseDate || a.createdAt) - new Date(b.releaseDate || b.createdAt)
    },
    {
      title: <span style={{ fontFamily: "'Rubik', sans-serif", fontWeight: 600, fontSize: 14 }}>Actions</span>,
      key: 'actions',
      width: 160,
      align: 'right',
      render: (_, record) => (
        <Space size={8}>
          <Tooltip title="Download">
            <Button
              icon={<DownloadOutlined />}
              href={record.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              style={{ 
                fontFamily: "'Rubik', sans-serif",
                fontSize: 13,
                color: '#470172', 
                borderColor: '#d9d9d9'
              }}
            />
          </Tooltip>
          <Button
            icon={<EditOutlined />}
            onClick={() => navigate(`/admin/policies/${record._id}`)}
            size="small"
            style={{ 
              fontFamily: "'Rubik', sans-serif",
              fontSize: 13,
              color: '#470172',
              borderColor: '#d9d9d9'
            }}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record._id)}
            size="small"
            style={{ 
              fontFamily: "'Rubik', sans-serif",
              fontSize: 13
            }}
          />
        </Space>
      )
    }
  ];

  if (loading && policies.length === 0) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#ffffff', fontFamily: "'Rubik', sans-serif" }}>
        <Content style={{ padding: '32px 48px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <Spin size="large" />
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#ffffff', fontFamily: "'Rubik', sans-serif" }}>
      <Content style={{ padding: '32px 48px' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <div>
            <Title level={2} style={{
              margin: 0,
              color: '#470172',
              fontFamily: "'Rubik', sans-serif",
              fontSize: 32,
              fontWeight: 600
            }}>
              Policy & Scheme Management
            </Title>
            <Text style={{ fontSize: 16, color: '#595959', fontFamily: "'Rubik', sans-serif" }}>
              Manage government policies and schemes documentation
            </Text>
          </div>
        </div>

        {/* Stats Cards */}
        <Row gutter={24} style={{ marginBottom: 32 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#fff', borderRadius: 8, minHeight: 120 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  background: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FileTextOutlined style={{ fontSize: 24, color: '#470172' }} />
                </div>
                <div>
                  <Text style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif", color: '#595959', display: 'block' }}>
                     Documents
                  </Text>
                  <Text style={{ color: '#470172', fontSize: 32, fontFamily: "'Rubik', sans-serif", fontWeight: 600 }}>
                    {totalPolicies}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#fff', borderRadius: 8, minHeight: 120 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  background: '#f6ffed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                </div>
                <div>
                  <Text style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif", color: '#595959', display: 'block' }}>
                    Policies
                  </Text>
                  <Text style={{ color: '#52c41a', fontSize: 32, fontFamily: "'Rubik', sans-serif", fontWeight: 600 }}>
                    {totalRegularPolicies}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#fff', borderRadius: 8, minHeight: 120 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  background: '#fff1f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FileTextOutlined style={{ fontSize: 24, color: '#f5222d' }} />
                </div>
                <div>
                  <Text style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif", color: '#595959', display: 'block' }}>
                    Schemes
                  </Text>
                  <Text style={{ color: '#f5222d', fontSize: 32, fontFamily: "'Rubik', sans-serif", fontWeight: 600 }}>
                    {totalSchemes}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card 
              bordered={false} 
              style={{ 
                background: '#470172', 
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                minHeight: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              hoverable
              onClick={() => navigate('/admin/policies/new')}
            >
              <div style={{ textAlign: 'center' }}>
                <PlusOutlined style={{ fontSize: 24, color: '#fff', marginBottom: 8 }} />
                <Text style={{ 
                  fontSize: 16, 
                  fontFamily: "'Rubik', sans-serif", 
                  color: '#fff',
                  fontWeight: 500,
                  display: 'block'
                }}>
                  Add New Document
                </Text>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Search and Filters */}
        <Card 
          bordered={false} 
          style={{ background: '#fff', borderRadius: 8, marginBottom: 24 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12} lg={8}>
              <Input
                size="large"
                placeholder="Search policies and schemes..."
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ fontFamily: "'Rubik', sans-serif", height: 40 }}
              />
            </Col>
            <Col xs={24} md={6} lg={4}>
              <Select
                size="large"
                value={filters.type}
                onChange={(value) => handleFilterChange('type', value)}
                placeholder="Type"
                style={{ width: '100%', fontFamily: "'Rubik', sans-serif" }}
                allowClear
              >
                <Option value="policy">Policy</Option>
                <Option value="scheme">Scheme</Option>
              </Select>
            </Col>
            <Col xs={24} md={6} lg={5}>
              <Select
                size="large"
                value={filters.sector}
                onChange={(value) => handleFilterChange('sector', value)}
                placeholder="Sector"
                style={{ width: '100%', fontFamily: "'Rubik', sans-serif" }}
                allowClear
                showSearch
                optionFilterProp="children"
                popupMatchSelectWidth={false}
                styles={{ popup: { root: { fontFamily: "'Rubik', sans-serif", minWidth: 220 } } }}
              >
                {sectorOptions.map((sector) => (
                  <Option key={sector} value={sector}>{sector}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} md={6} lg={4}>
              <Select
                size="large"
                value={filters.isInternational}
                onChange={(value) => handleFilterChange('isInternational', value)}
                placeholder="Scope"
                style={{ width: '100%', fontFamily: "'Rubik', sans-serif" }}
                allowClear
              >
                <Option value="false">National</Option>
                <Option value="true">International</Option>
              </Select>
            </Col>
            <Col xs={24} md={6} lg={4}>
              <Button
                size="large"
                block
                onClick={clearFilters}
                icon={<FilterOutlined />}
                style={{
                  fontFamily: "'Rubik', sans-serif",
                  fontSize: 14,
                  color: '#470172',
                  borderColor: '#d9d9d9'
                }}
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Policies Table */}
        <Card
          title={
            <div>
              <p style={{ fontFamily: "'Rubik', sans-serif", fontSize: 15, fontWeight: 400, color: '#470172', margin: '4px 0 0 0', opacity: 0.7 }}>
                List of all policies and schemes
              </p>
            </div>
          }
          extra={
            <Button
              icon={<ReloadOutlined spin={loading} />}
              onClick={fetchPolicies}
              disabled={loading}
              style={{
                fontFamily: "'Rubik', sans-serif",
                fontSize: 14,
                fontWeight: 400,
                color: '#470172',
                borderColor: '#470172'
              }}
            >
              Refresh
            </Button>
          }
          bordered={false}
          style={{ background: '#fff', borderRadius: 8 }}
        >
          {policies.length === 0 && !loading ? (
            <Empty
              description={
                <span style={{ fontFamily: "'Rubik', sans-serif", fontSize: 16, fontWeight: 400, color: '#470172' }}>
                  No policies or schemes found. Get started by adding a new document.
                </span>
              }
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/admin/policies/new')}
                size="large"
                style={{
                  fontFamily: "'Rubik', sans-serif",
                  fontSize: 16,
                  fontWeight: 400,
                  backgroundColor: '#470172',
                  borderColor: '#470172'
                }}
              >
                Add First Document
              </Button>
            </Empty>
          ) : (
            <Table
              columns={columns}
              dataSource={policies}
              rowKey="_id"
              loading={loading}
              pagination={{ 
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50'],
                showTotal: (total) => (
                  <span style={{ fontFamily: "'Rubik', sans-serif", fontSize: 14 }}>
                    Total {total} items
                  </span>
                ),
                responsive: true
              }}
              style={{ fontFamily: "'Rubik', sans-serif" }}
              scroll={{ x: 800 }}
            />
          )}
        </Card>
      </Content>
    </Layout>
  );
};

export default PolicyManagement;
